import React, { createContext, useContext, useEffect, useState } from 'react'
import { SuperAdmin, Permission, AuthContext as IAuthContext } from '@/types/shared'
import { authApi } from '@/api/authApi'
import { clearInvalidTokens, clearAllTokens, getStoredTokens, isValidJwtFormat } from '@/utils/tokenUtils'
import { getBasicDeviceInfo, getDeviceId, getDeviceInfo } from '@/utils/device'

interface AuthProviderProps {
  children: React.ReactNode
}

const AuthContext = createContext<IAuthContext | undefined>(undefined)

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<SuperAdmin | null>(null)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        clearInvalidTokens()

        const { accessToken, refreshToken } = getStoredTokens()

        if (!accessToken) {
          setIsLoading(false)
          return
        }

        if (refreshToken && isValidJwtFormat(refreshToken)) {
          try {
            console.log('🔄 Refreshing token on page load...')
            const refreshResponse = await authApi.refreshToken()

            localStorage.setItem('accessToken', refreshResponse.accessToken)
            localStorage.setItem('refreshToken', refreshResponse.refreshToken)

            console.log('✅ Token refreshed successfully on page load')

            // Get current user with fresh token
            const response = await authApi.getCurrentUser()
            setUser(response.user as SuperAdmin)
            setPermissions(response.permissions)
          } catch (refreshError) {
            console.log('⚠️ Token refresh failed on page load, trying with existing token...')
            // Fallback to existing token
            if (isValidJwtFormat(accessToken)) {
              const response = await authApi.getCurrentUser()
              setUser(response.user as SuperAdmin)
              setPermissions(response.permissions)
            } else {
              console.warn('Invalid access token format, clearing auth...')
              clearAllTokens()
            }
          }
        } else if (isValidJwtFormat(accessToken)) {
          // No refresh token available, use existing access token
          const response = await authApi.getCurrentUser()
          setUser(response.user as SuperAdmin)
          setPermissions(response.permissions)
        } else {
          console.warn('Invalid access token format, clearing auth...')
          clearAllTokens()
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        clearAllTokens()
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string) => {
    // Use basic device info (sync, no Firebase) to avoid blocking login
    const deviceInfo = getBasicDeviceInfo();

    const response = await authApi.login({ email, password }, deviceInfo)

    console.log('Login response received:', response)

    // Store access token (always present)
    if (isValidJwtFormat(response.accessToken)) {
      localStorage.setItem('accessToken', response.accessToken)
      console.log('Access token stored successfully')
    } else {
      console.error('Invalid access token format:', response.accessToken)
      throw new Error('Invalid access token received from server')
    }

    // Store refresh token if present and valid
    if (response.refreshToken && isValidJwtFormat(response.refreshToken)) {
      localStorage.setItem('refreshToken', response.refreshToken)
      console.log('Refresh token stored successfully')
    } else {
      // Clear any existing invalid refresh token
      localStorage.removeItem('refreshToken')
      console.warn('No valid refresh token received from server')
    }

    console.log('Setting user:', response.user)
    setUser(response.user as SuperAdmin)
    setPermissions(response.permissions)

    console.log('Login completed, user should be redirected based on role:', response.user.role)

    // Register device with FCM token in the background (don't block login)
    getDeviceInfo().then((fullDeviceInfo) => {
      if (fullDeviceInfo.fcmToken) {
        authApi.registerDevice(fullDeviceInfo).then(() => {
          console.log('Device registered with FCM token for push notifications')
        }).catch((err) => {
          console.warn('Failed to register device with FCM token:', err)
        })
      }
    }).catch(() => {
      // Firebase not available — push notifications disabled
    })

    // Return the full response including onboarding info
    return response
  }

  const loginWithPhonePin = async (phone: string, pin: string, tenantSlug?: string) => {
    // Use basic device info (sync, no Firebase) to avoid blocking login
    const deviceInfo = getBasicDeviceInfo();

    const response = await authApi.loginWithPhonePin(phone, pin, tenantSlug, deviceInfo)

    console.log('Phone/PIN login response received:', response)

    // Handle 2FA requirement
    if (response.requiresTwoFactor) {
      console.log('2FA required for phone/PIN login')
      return response
    }

    // Store access token (always present)
    if (isValidJwtFormat(response.accessToken)) {
      localStorage.setItem('accessToken', response.accessToken)
      console.log('Access token stored successfully')
    } else {
      console.error('Invalid access token format:', response.accessToken)
      throw new Error('Invalid access token received from server')
    }

    // Store refresh token if present and valid
    if (response.refreshToken && isValidJwtFormat(response.refreshToken)) {
      localStorage.setItem('refreshToken', response.refreshToken)
      console.log('Refresh token stored successfully')
    } else {
      // Clear any existing invalid refresh token
      localStorage.removeItem('refreshToken')
      console.warn('No valid refresh token received from server')
    }

    console.log('Setting user:', response.user)
    setUser(response.user as SuperAdmin)
    setPermissions(response.permissions)

    console.log('Phone/PIN login completed, user should be redirected based on role:', response.user.role)

    // Register device with FCM token in the background (don't block login)
    getDeviceInfo().then((fullDeviceInfo) => {
      if (fullDeviceInfo.fcmToken) {
        authApi.registerDevice(fullDeviceInfo).then(() => {
          console.log('Device registered with FCM token for push notifications')
        }).catch((err) => {
          console.warn('Failed to register device with FCM token:', err)
        })
      }
    }).catch(() => {
      // Firebase not available — push notifications disabled
    })

    // Return the full response including onboarding info
    return response
  }

  const logout = async () => {
    try {
      // Get device ID for unregistering
      const deviceId = getDeviceId();
      await authApi.logout(deviceId)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAllTokens()
      setUser(null)
      setPermissions([])
    }
  }

  const value: IAuthContext = {
    user,
    tenant: null, // Super admin doesn't belong to a tenant
    permissions,
    isAuthenticated: !!user,
    isSuperAdmin: true,
    accessLevel: 'platform',
    login,
    loginWithPhonePin,
    logout,
    isLoading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

// Enhanced hook with auth actions
export function useAuth() {
  const context = useAuthContext()
  return context
}
